import { AxiosResponse } from 'axios';
import { apiClient } from '../instance';
import type {
  DeviceIntakePayload,
  DeviceModel,
  DeviceResultPayload,
  DiagnosticCase,
  DiagnosticDevice,
  DiagnosticReportFilters,
  DiagnosticReportRow,
  DiagnosticReportSummary,
  DiagnosticTicket,
  DiagnosticTicketCreatePayload,
  DiagnosticTypeInfo,
  FaultReason,
  Housing,
  Paginated,
  SerialLookupResponse,
} from '../../types/diagnosticsInterfaces';

const BASE = '/diagnostics';

/**
 * LimitOffsetPagination в ядре без PAGE_SIZE: envelope {count, results} приходит
 * только при ?limit=, иначе — голый массив. Нормализуем оба варианта.
 */
export function asList<T>(data: Paginated<T> | T[]): T[] {
  return Array.isArray(data) ? data : data?.results ?? [];
}

/** Убирает пустые значения; массивы разворачиваются в повторяющиеся ключи (?model=1&model=2). */
function reportParams(filters: DiagnosticReportFilters): URLSearchParams {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.append(key, String(value));
    }
  });
  return params;
}

export const diagnosticsApi = {
  /* ── диагностические типы тикетов ── */
  getTypes: (): Promise<AxiosResponse<DiagnosticTypeInfo[]>> =>
    apiClient.get<DiagnosticTypeInfo[]>(`${BASE}/types/`),

  /* ── справочники ── */
  getDeviceModels: (q = ''): Promise<AxiosResponse<Paginated<DeviceModel>>> =>
    apiClient.get<Paginated<DeviceModel>>(`${BASE}/device-models/`, {
      params: { active: 1, ...(q ? { q } : {}), limit: 200 },
    }),
  createDeviceModel: (data: Pick<DeviceModel, 'name'> & Partial<DeviceModel>) =>
    apiClient.post<DeviceModel>(`${BASE}/device-models/`, data),

  getHousings: (): Promise<AxiosResponse<Paginated<Housing>>> =>
    apiClient.get<Paginated<Housing>>(`${BASE}/housings/`, { params: { limit: 200 } }),

  getFaultReasons: (q = ''): Promise<AxiosResponse<Paginated<FaultReason>>> =>
    apiClient.get<Paginated<FaultReason>>(`${BASE}/fault-reasons/`, {
      params: { limit: 50, ...(q ? { q } : {}) },
    }),

  /* ── тикеты ── */
  getTickets: (params: {
    ticket_type?: number;
    limit?: number;
    offset?: number;
    ordering?: string;
  } = {}): Promise<AxiosResponse<Paginated<DiagnosticTicket>>> =>
    apiClient.get<Paginated<DiagnosticTicket>>(`${BASE}/tickets/`, { params }),

  getTicketById: (id: number): Promise<AxiosResponse<DiagnosticTicket>> =>
    apiClient.get<DiagnosticTicket>(`${BASE}/tickets/${id}/`),

  createTicket: (
    data: DiagnosticTicketCreatePayload,
  ): Promise<AxiosResponse<DiagnosticTicket>> =>
    apiClient.post<DiagnosticTicket>(`${BASE}/tickets/`, data),

  getCase: (ticketId: number): Promise<AxiosResponse<DiagnosticCase>> =>
    apiClient.get<DiagnosticCase>(`${BASE}/tickets/${ticketId}/case/`),
  patchCase: (
    ticketId: number,
    data: Partial<Pick<DiagnosticCase, 'notify_client'>>,
  ): Promise<AxiosResponse<DiagnosticCase>> =>
    apiClient.patch<DiagnosticCase>(`${BASE}/tickets/${ticketId}/case/`, data),

  /* ── устройства тикета ── */
  addDevice: (
    ticketId: number,
    data: DeviceIntakePayload,
  ): Promise<AxiosResponse<DiagnosticDevice>> =>
    apiClient.post<DiagnosticDevice>(`${BASE}/tickets/${ticketId}/devices/`, data),

  getDevice: (
    ticketId: number,
    deviceId: number,
  ): Promise<AxiosResponse<DiagnosticDevice>> =>
    apiClient.get<DiagnosticDevice>(`${BASE}/tickets/${ticketId}/devices/${deviceId}/`),

  updateDevice: (
    ticketId: number,
    deviceId: number,
    data: DeviceResultPayload | DeviceIntakePayload,
  ): Promise<AxiosResponse<DiagnosticDevice>> =>
    apiClient.patch<DiagnosticDevice>(
      `${BASE}/tickets/${ticketId}/devices/${deviceId}/`,
      data,
    ),

  deleteDevice: (ticketId: number, deviceId: number): Promise<AxiosResponse<void>> =>
    apiClient.delete<void>(`${BASE}/tickets/${ticketId}/devices/${deviceId}/`),

  /* ── дедуп ── */
  lookupSerial: (
    serial: string,
    excludeTicket?: number,
  ): Promise<AxiosResponse<SerialLookupResponse>> =>
    apiClient.get<SerialLookupResponse>(`${BASE}/devices/lookup/`, {
      params: { serial, ...(excludeTicket ? { exclude_ticket: excludeTicket } : {}) },
    }),

  /* ── отчёт ── */
  getReport: (
    filters: DiagnosticReportFilters,
    page = 1,
    limit = 50,
  ): Promise<AxiosResponse<Paginated<DiagnosticReportRow>>> => {
    const params = reportParams(filters);
    params.set('limit', String(limit));
    params.set('offset', String((page - 1) * limit));
    return apiClient.get<Paginated<DiagnosticReportRow>>(`${BASE}/reports/devices/`, {
      params,
    });
  },

  getReportSummary: (
    filters: DiagnosticReportFilters,
  ): Promise<AxiosResponse<DiagnosticReportSummary>> =>
    apiClient.get<DiagnosticReportSummary>(`${BASE}/reports/devices/summary/`, {
      params: reportParams(filters),
    }),

  exportReport: (filters: DiagnosticReportFilters): Promise<AxiosResponse<Blob>> =>
    apiClient.get<Blob>(`${BASE}/reports/devices/export/`, {
      params: reportParams(filters),
      responseType: 'blob',
    }),
};
